import { apiClient } from "./client";
import type { Paginated, PageQuery } from "@/types/common";
import type { LeadSource } from "@/types/enums";

export interface RegTargets {
  reg: number;
  trial: number;
  lesson: number;
}

export interface RegistrationRow {
  id: string;
  branchId: string;
  branchName?: string;
  date: string;
  source: LeadSource;
  fullName: string;
  age: number | null;
  phone: string;
  groupCode: string | null;
  isNewGroup: boolean;
  subjectName: string;
  adminUserId: string | null;
  adminName: string | null;
  arriveDate: string | null;
  trialAttended: boolean;
  remindDate: string | null;
  lessonAttended: boolean;
  left: boolean;
  enrolled: boolean;
  studentId: string | null;
}

export interface RegistrationKpi {
  total: number;
  trial: number;
  lesson: number;
  waiting: number;
  targets: RegTargets;
}

export interface RegistrationsResponse extends Paginated<RegistrationRow> {
  kpi: RegistrationKpi;
}

export interface RegistrationInput {
  branchId: string;
  date: string;
  source?: LeadSource;
  fullName: string;
  age?: number;
  phone: string;
  groupCode?: string;
  isNewGroup?: boolean;
  subjectName: string;
  adminUserId?: string;
  arriveDate?: string;
  remindDate?: string;
}

export async function fetchRegistrations(query: PageQuery & { branchId?: string; search?: string } = {}): Promise<RegistrationsResponse> {
  const { data } = await apiClient.get<RegistrationsResponse>("/registrations", { params: query });
  return data;
}

export async function createRegistration(payload: RegistrationInput): Promise<RegistrationRow> {
  const { data } = await apiClient.post<RegistrationRow>("/registrations", payload);
  return data;
}

export async function updateRegistration(id: string, payload: Partial<RegistrationInput>): Promise<RegistrationRow> {
  const { data } = await apiClient.patch<RegistrationRow>(`/registrations/${id}`, payload);
  return data;
}

export async function toggleRegistrationTrial(id: string): Promise<RegistrationRow> {
  const { data } = await apiClient.patch<RegistrationRow>(`/registrations/${id}/toggle-trial`);
  return data;
}

export async function toggleRegistrationLesson(id: string): Promise<RegistrationRow> {
  const { data } = await apiClient.patch<RegistrationRow>(`/registrations/${id}/toggle-lesson`);
  return data;
}

export async function markRegistrationLeft(id: string): Promise<RegistrationRow> {
  const { data } = await apiClient.patch<RegistrationRow>(`/registrations/${id}/mark-left`);
  return data;
}

export async function enrollRegistration(id: string, groupId?: string): Promise<{ registration: RegistrationRow }> {
  const { data } = await apiClient.post<{ registration: RegistrationRow }>(`/registrations/${id}/enroll`, { groupId });
  return data;
}

export async function fetchRegTargets(branchId: string): Promise<RegTargets> {
  const { data } = await apiClient.get<RegTargets>(`/registrations/targets/${branchId}`);
  return data;
}

export async function updateRegTargets(branchId: string, targets: RegTargets): Promise<RegTargets> {
  const { data } = await apiClient.patch<RegTargets>(`/registrations/targets/${branchId}`, targets);
  return data;
}
